import React, { ChangeEventHandler, forwardRef, useState } from 'react';
import { isNil } from 'lodash';
import classnames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { Counter } from '@fiverr-private/data_display';
import { renderChildren } from '@fiverr-private/ui_utils';
import { ExclamationOutlineIcon } from '@fiverr-private/icons';
import { red_1000 } from '@fiverr-private/sass/helpers';
import classes from './index.module.scss';
import { TextareaProps } from './types';

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      children,
      disabled,
      showCounter,
      className,
      isSuccess = false,
      isError = false,
      maxLength,
      counterNumberFormat,
      value,
      defaultValue,
      onChange = Function.prototype,
      resize = false,
      errorMessage,
      message,
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useState(defaultValue || children);
    const currentValue = isNil(value) ? innerValue : value;

    const props: TextareaProps = {
      disabled,
      maxLength,
      value: currentValue,
      ...rest,
    };

    const displayValue = (currentValue || '').toString();
    const maxLengthExceeded = !isNil(maxLength) && displayValue.length > maxLength;
    const hasError = isError || maxLengthExceeded;
    const messageNode = message || errorMessage;
    const hasErrorMessage = (isError && messageNode) || maxLengthExceeded;
    const hasNeutralMessage = !isError && !isSuccess && message;

    const classNames = classnames(
      classes.textareaContainer,
      {
        [classes.disabled]: disabled,
        [classes.success]: isSuccess,
        [classes.error]: hasError,
        [classes.resize]: resize,
      },
      className
    );

    const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      setInnerValue(e.target.value);
      onChange(e);
    };

    return (
      <>
        <textarea {...props} role="textbox" className={classNames} ref={ref} onChange={handleChange} />
        {(showCounter || hasError) && (
          <div data-testid="bottom-area" className={classnames(classes.bottomArea, { [classes.error]: hasError })}>
            {(hasNeutralMessage || hasErrorMessage) && (
              <div className={classes.messageWrapper}>
                {hasErrorMessage && <ExclamationOutlineIcon color={red_1000} />}
                {errorMessage || (message && !maxLengthExceeded)
                  ? renderChildren(messageNode)
                  : maxLengthExceeded && <I18n k="inputs.textarea.maxLengthExceeded" params={{ maxLength }} />}
              </div>
            )}

            {showCounter && (
              <span className={classes.counter}>
                <Counter current={displayValue.length} max={maxLength} format={counterNumberFormat} />
                &nbsp;
                <I18n k={`inputs.textarea.character${displayValue.length === 1 && !maxLength ? '' : 's'}`} />
              </span>
            )}
          </div>
        )}
      </>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
